import { DocumentDecisionButtons } from './DocumentDecisionButtons';
import { codeLabel } from '../../utils/codeLabel';
import { partyLabel } from '../../components/domain/partyLabel';
import { ErrorState, LoadingState, StaleBanner } from '../../components/ui/AsyncState';
import { useQueryClient } from '@tanstack/react-query';
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { useRecoveryQuery as useQuery } from '../../components/ui/useRecoveryQuery';
import { OPERATIONAL_POLL_INTERVAL_MS, operationalQueryPolicy } from '../../api/queryPolicies';
import {
  getShipmentById,
  verifyDocument,
  getUserById,
  getBidsForShipment,
  getDocumentsForShipment,
  getTrackingHistory,
  cancelShipment,
  completeShipment,
  openStop as openStopApi,
  editStop as editStopApi,
  closeStop as closeStopApi,
  repostShipment,
  reverseAward,
  acceptBid,
  getAdminInterests,
  recomputeShipmentDistance,
  listShipperMessages,
  recordShipmentDelay,
} from '../../api/endpoints/admin';
import { isDemo } from '../../demo/flag';
import { LaneHint } from '../profit/LaneHint';
import { ProfitCell } from '../profit/ProfitCell';
import { RecordDelaySheet } from './RecordDelaySheet';
import { TruckStoppedDialog } from './TruckStoppedDialog';
import { DetentionCard } from './DetentionCard';
import { StopHistoryCard } from './StopHistoryCard';
import { STOP_INVALIDATE_KEYS, stopsQueryKey } from './stopQueries';
import { ShipperMessagesTab } from './ShipperMessagesTab';
import { delayFailureCopy, delayReasonLabel, formatIst } from './delayReason';
import { shipmentStatusLabel } from './shipmentStatusLabel';
import { StatusPill } from '../../components/ui/StatusPill';
import { StatusIcon } from '../../components/ui/StatusIcon';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { toast } from '../../components/ui/Toast';
import { useMutationHook } from '../../hooks/useMutationHook';
import { actionFailureMessage } from '../../api/actionFailure';
import {
  bidStatusDisplay,
  shipmentStatusColor,
  interestStatusColor,
} from '../../components/domain/statusColor';
import { UserDetailDrawer } from '../../components/domain/UserDetailDrawer';
import { ShipmentFormModal } from './ShipmentFormModal';
import { EditStopAddressModal } from './EditStopAddressModal';
import { canEditDrop, canEditPickup } from './addressEditRules';
import { FleetManagerSuggestions } from './FleetManagerSuggestions';
import { TruckIcon } from './TruckIcon';
import { ApproxMarker } from './ApproxMarker';
import { findTruckType } from '../../config/truckCatalog';
import { truckTypeLabel } from './truckTypeLabel';
import { curr, marginLabel } from '../../utils/price';
import { RateSourcePill } from './RateSourcePill';
import { tons } from '../../utils/weight';
import { buildMapsUrl } from '../../utils/maps';
import { formatTrackingLocation, getGoogleMapsUrl } from '../../utils/tracking';
import { trackingEventSourceNote, trackingEventTitle } from './trackingEventTitle';
import { formatDistance, formatDuration } from '../../utils/distance';
import { BidFormModal } from './BidFormModal';
import { EditShipperFreightDialog } from './EditShipperFreightDialog';
import { EditPartnerPriceDialog } from './EditPartnerPriceDialog';
import { partnerPriceEditable } from './partnerOfferPrice';
import { bidShipperMeta, serverMarginText } from './bidPricing';
import { SHIPPER_FREIGHT_LOCKED_HINT } from './shipperFreight';
import { Button } from '../../components/ui/Button';
import { AwardSummary } from './AwardSummary.tsx';
import { ReverseAwardDialog, type ReverseAwardChoice } from './ReverseAwardDialog';
import { reverseAwardReasonError, reverseAwardReasonLabel } from './reverseAwardReason';
import { DnmRequestFailure } from '../../api/dnmFailure';
import { TrackingEventModal } from './TrackingEventModal';
import { DocumentUploadModal } from './DocumentUploadModal';
import { AssignDriverModal } from './AssignDriverModal';
import { InterestDetailModal } from '../interests/InterestDetailModal';
import type { BidResponse } from '../../api/endpoints/admin';
import type {
  DocumentResponse,
  RecordDelayRequest,
  OpenStopRequest,
  EditStopRequest,
  ShipmentResponse,
  ShipmentStatus,
  AdminInterestSummaryResponse,
  AdminInterestDetailResponse,
} from '../../api/types';
import { ShipmentMilestoneStepper } from './ShipmentMilestoneStepper';
import { AdvanceStatusModal } from './AdvanceStatusModal';
import { ParticipantKycSection } from './ParticipantKycSection';
import { bidAwardBlocker, gateFor, kycGate } from './kycGates';
import { Notice } from '../../components/ui/Notice';
import { InTransitLocationModal } from './InTransitLocationModal';
import { RefreshButton } from '../../components/ui/RefreshButton';
import { ShipmentNotifications } from './notifications/ShipmentNotifications';
import { OwnershipPanel } from '../ownership/OwnershipPanel';
import styles from './ShipmentDetailView.module.css';
import { DialogTitleRow } from '../../help/DialogTitleRow';
import {
  getOrderMoneySummary,
  mapPaymentError,
  setPartnerBill,
} from '../../api/endpoints/payments';
import { OrderPaymentsPanel } from '../payments/OrderPaymentsPanel';
import { AddChargeDialog } from '../payments/AddChargeDialog';
import { ReviewPartnerChargeFlow } from '../payments/ReviewPartnerChargeFlow';
import { useAddCharge, useApproveCharge, useRejectCharge, useVoidCharge } from '../payments/hooks';
import { useOpsUser } from '../ownership/opsUser';
import { formatIstDateTime } from '../payments/paymentDates';
import { driverAcceptance, formatAcceptedAt } from './driverAcceptance';
import { ReasonDialog } from '../payments/ReasonDialog';
import { PartnerBillDialog } from '../payments/PartnerBillDialog';
import { formatRoute } from '../../utils/routeLabel';

/** "Driver" row on the Carrier Assignment Status card (#290, R17): Accepted / Not accepted / hidden. */
function DriverAcceptanceRow({
  status,
  assignment,
}: {
  status: string;
  assignment: ShipmentResponse['assignment'];
}) {
  const { t } = useTranslation();
  const acceptance = driverAcceptance(status, assignment);
  if (!acceptance) return null;
  return (
    <div className={styles.detailRow} data-testid="driver-acceptance">
      <span className={styles.label}>{t('shipments.shipmentDetail.driverAcceptance.driver')}</span>
      <span className={`${styles.val} ${styles.acceptance}`}>
        {acceptance.kind === 'accepted' ? (
          <>
            <StatusPill
              bg="var(--status-green-bg)"
              text="var(--status-green-text)"
              label={t('shipments.shipmentDetail.driverAcceptance.accepted')}
              icon={<StatusIcon name="check" />}
            />
            <span className={styles.acceptedAt}>{formatAcceptedAt(acceptance.at)}</span>
          </>
        ) : (
          <StatusPill
            bg="var(--status-amber-bg)"
            text="var(--status-amber-text)"
            label={t('shipments.shipmentDetail.driverAcceptance.notAccepted')}
            title={t('shipments.shipmentDetail.driverAcceptance.notAcceptedHint')}
            icon={<StatusIcon name="hourglass" />}
          />
        )}
      </span>
    </div>
  );
}

type Tab =
  | 'overview'
  | 'payments'
  | 'interests'
  | 'bids'
  | 'documents'
  | 'tracking'
  | 'assignment'
  | 'messages';

/**
 * Shipper zero calls (#294): the Record-delay action, the Messages tab and /track/:code run on
 * the demo world only until rhino-api ships the dnm-work#150 Contract, so the dev console never
 * calls a missing endpoint (engineering principle 3). The build issue removes this gate.
 */
const SHIPPER_ZERO_CALLS = isDemo();

/** Statuses a stop can be opened in (dnm-work#165 C2); outside them the API answers STOP_NOT_ALLOWED. */
const STOPPABLE_STATUSES: readonly ShipmentStatus[] = [
  'ASSIGNED',
  'EN_ROUTE_PICKUP',
  'AT_PICKUP',
  'LOADED',
  'IN_TRANSIT',
  'AT_DROP',
];

/** Delay row on the overview (#294): reason, new expected time and when ops recorded it, in IST. */
function DelayCard({ delay }: { delay: NonNullable<ShipmentResponse['delay']> }) {
  const { t } = useTranslation();
  return (
    <div className={`${styles.card} ${styles.delayCard}`} data-testid="shipment-delay">
      <h3 className={styles.cardTitle}>
        <span className={styles.delayTitle}>
          <StatusIcon name="schedule" size={16} />
          {t('shipments.shipmentDetail.delay.title')}
        </span>
      </h3>
      <div className={styles.detailRow}>
        <span className={styles.label}>{t('shipments.shipmentDetail.delay.reason')}</span>
        <span className={styles.val}>{delayReasonLabel(delay)}</span>
      </div>
      <div className={styles.detailRow}>
        <span className={styles.label}>{t('shipments.shipmentDetail.delay.newExpectedTime')}</span>
        <span className={styles.val}>{formatIst(delay.newExpectedAt)}</span>
      </div>
      <div className={styles.detailRow}>
        <span className={styles.label}>{t('shipments.shipmentDetail.delay.recorded')}</span>
        <span className={styles.val}>{t('shipments.shipmentDetail.delay.recordedBy', { time: formatIst(delay.recordedAt) })}</span>
      </div>
    </div>
  );
}

function fmtDate(iso: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function ShipmentDetailView() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as {
    from?: string;
    fromLabel?: string;
    restoreState?: unknown;
  } | null;
  const stateFrom = locationState?.from;
  const stateFromLabel = locationState?.fromLabel;
  const stateRestore = locationState?.restoreState;

  const getBackPath = () => {
    if (stateFrom) return stateFrom;
    return '/shipments';
  };

  const getBackLabel = () => {
    if (stateFromLabel) return `← Back to ${stateFromLabel}`;
    if (stateFrom) {
      if (stateFrom.startsWith('/orders')) return '← Back to Orders';
      if (stateFrom.startsWith('/stopped')) return '← Back to Stopped trucks';
      if (stateFrom.startsWith('/users')) return '← Back to User';
      if (stateFrom.startsWith('/interests')) return '← Back to Interests';
      return '← Back';
    }
    return '← Back to Shipments';
  };

  const { id } = useParams<{ id: string }>();
  const shipmentId = Number(id);
  const isTerminal = (status?: string) =>
    status === 'COMPLETED' || status === 'CANCELLED' || status === 'EXPIRED';
  const isDeliveredOrTerminal = (status?: string) =>
    status === 'AT_DROP' ||
    status === 'DELIVERED' ||
    status === 'COMPLETED' ||
    status === 'CANCELLED' ||
    status === 'EXPIRED';
  const [activeTab, setActiveTab] = useState<Tab>(() =>
    new URLSearchParams(location.search).get('tab') === 'documents'
      ? 'documents'
      : new URLSearchParams(location.search).get('tab') === 'payments'
        ? 'payments'
        : 'overview',
  );
  useEffect(() => {
    const linkedTab = new URLSearchParams(location.search).get('tab');
    if (linkedTab === 'payments' || linkedTab === 'documents') setActiveTab(linkedTab);
    if (linkedTab === 'messages' && SHIPPER_ZERO_CALLS) setActiveTab('messages');
  }, [location.search, shipmentId]);
  const [confirmAction, setConfirmAction] = useState<
    | 'cancel'
    | 'complete'
    | 'movingAgain'
    | 'repost'
    | 'reverseAward'
    | 'acceptBid'
    | 'recomputeDistance'
    | null
  >(null);
  const [selectedBidId, setSelectedBidId] = useState<number | null>(null);
  /** Refusal reason for the open action dialog; cleared when a dialog opens, retries or closes. */
  const [dialogError, setDialogError] = useState<string | null>(null);
  useEffect(() => setDialogError(null), [confirmAction]);
  const [documentReason, setDocumentReason] = useState('');
  const [documentError, setDocumentError] = useState<string | null>(null);
  const documentClient = useQueryClient();
  const [documentBusy, setDocumentBusy] = useState(false);
  const documentGuard = useRef(false);
  const [selectedDoc, setSelectedDoc] = useState<DocumentResponse | null>(null);
  const [shipperDrawerOpen, setShipperDrawerOpen] = useState(false);
  const [isEditShipmentOpen, setIsEditShipmentOpen] = useState(false);
  const [editStop, setEditStop] = useState<'PICKUP' | 'DROP' | null>(null);
  const [isAssignDriverOpen, setIsAssignDriverOpen] = useState(false);
  const [assignDriverInitialMode, setAssignDriverInitialMode] = useState<
    'edit_details' | 'change_driver'
  >('change_driver');

  const handleOpenAssignDriver = (mode: 'edit_details' | 'change_driver' = 'change_driver') => {
    setAssignDriverInitialMode(mode);
    setIsAssignDriverOpen(true);
  };
  const [bidModal, setBidModal] = useState<'closed' | 'create' | BidResponse>('closed');
  const [shipperFreightOpen, setShipperFreightOpen] = useState(false);
  const [partnerPriceOpen, setPartnerPriceOpen] = useState(false);
  const [bidPrefillId, setBidPrefillId] = useState<number | null>(null);
  const [isAddEventOpen, setIsAddEventOpen] = useState(false);
  const [isAddDocOpen, setIsAddDocOpen] = useState(false);
  const [advanceStatus, setAdvanceStatus] = useState<ShipmentStatus | null>(null);
  const [isInTransitLocationOpen, setIsInTransitLocationOpen] = useState(false);
  const [showPriorCycles, setShowPriorCycles] = useState(false);
  const [selectedInterestId, setSelectedInterestId] = useState<number | null>(null);
  const [interestToConvert, setInterestToConvert] = useState<
    AdminInterestSummaryResponse | AdminInterestDetailResponse | null
  >(null);
  const [notificationDrawerOpen, setNotificationDrawerOpen] = useState(false);
  const [delaySheetOpen, setDelaySheetOpen] = useState(false);
  const [delayError, setDelayError] = useState<string | null>(null);
  const [isAddChargeOpen, setIsAddChargeOpen] = useState(false);
  const [chargeReason, setChargeReason] = useState<{
    kind: 'reject' | 'void';
    chargeId: number;
  } | null>(null);
  const [chargeActionError, setChargeActionError] = useState<string | null>(null);
  const [billDialogOpen, setBillDialogOpen] = useState(
    new URLSearchParams(location.search).get('partnerBill') === '1',
  );
  const [billSaving, setBillSaving] = useState(false);
  const [billError, setBillError] = useState<string | null>(null);
  const billGuard = useRef(false);
  const { isManager } = useOpsUser();
  const addChargeMut = useAddCharge();
  const [reviewPartnerChargeId, setReviewPartnerChargeId] = useState<number | null>(null);
  const approveChargeMut = useApproveCharge();
  const rejectChargeMut = useRejectCharge();
  const voidChargeMut = useVoidCharge();

  // Reset drawer state when navigating between shipments
  useEffect(() => {
    setNotificationDrawerOpen(false);
  }, [shipmentId]);

  const {
    data: shipment,
    isLoading: isShipmentLoading,
    isError: isShipmentError,
    refetch: refetchShipment,
  } = useQuery({
    queryKey: ['admin', 'shipments', 'detail', shipmentId],
    queryFn: () => getShipmentById(shipmentId),
    ...operationalQueryPolicy,
    refetchInterval: (query) =>
      isTerminal((query.state.data as ShipmentResponse | undefined)?.status)
        ? false
        : OPERATIONAL_POLL_INTERVAL_MS,
  });

  const {
    data: bids,
    isLoading: isBidsLoading,
    isError: isBidsError,
    refetch: refetchBids,
  } = useQuery({
    queryKey: ['admin', 'shipments', 'bids', shipmentId],
    queryFn: () => getBidsForShipment(shipmentId),
    ...operationalQueryPolicy,
    refetchInterval: isTerminal(shipment?.status) ? false : OPERATIONAL_POLL_INTERVAL_MS,
    enabled: !!shipment && activeTab === 'bids',
  });
  const selectedBid = bids?.find((b) => b.id === selectedBidId);
  const selectedBidLabel =
    selectedBid &&
    partyLabel({
      company: selectedBid.bidderCompanyName,
      name: selectedBid.bidderName,
      fallback: `bidder #${selectedBid.bidderId}`,
    }).primary;

  const {
    data: interestsData,
    isLoading: isInterestsLoading,
    isError: isInterestsError,
    refetch: refetchInterests,
  } = useQuery({
    queryKey: ['admin', 'interests', 'shipment', shipment?.shipmentUuid],
    queryFn: () =>
      getAdminInterests({ shipmentUuid: shipment!.shipmentUuid || undefined, size: 50 }),
    enabled: !!shipment?.shipmentUuid,
    ...operationalQueryPolicy,
    refetchInterval:
      activeTab === 'interests' && !isTerminal(shipment?.status)
        ? OPERATIONAL_POLL_INTERVAL_MS
        : false,
  });

  const distinctInterestsCount = interestsData
    ? (interestsData.totalItems ?? interestsData.items?.length ?? 0)
    : '—';

  const {
    data: documents,
    isLoading: isDocumentsLoading,
    isError: isDocumentsError,
    refetch: refetchDocuments,
  } = useQuery({
    queryKey: ['admin', 'shipments', 'documents', shipmentId],
    queryFn: () => getDocumentsForShipment(shipmentId),
    ...operationalQueryPolicy,
    refetchInterval: isTerminal(shipment?.status) ? false : OPERATIONAL_POLL_INTERVAL_MS,
    enabled: !!shipment && activeTab === 'documents',
  });

  const openedDocumentLink = useRef('');
  useEffect(() => {
    const docId = Number(new URLSearchParams(location.search).get('documentId'));
    if (
      activeTab === 'documents' &&
      documents &&
      docId > 0 &&
      openedDocumentLink.current !== `${shipmentId}:${docId}`
    ) {
      openedDocumentLink.current = `${shipmentId}:${docId}`;
      setSelectedDoc(documents.find((d) => d.id === docId) ?? null);
    }
  }, [documents, location.search, activeTab, shipmentId]);
  const {
    data: tracking,
    isLoading: isTrackingLoading,
    isError: isTrackingError,
    refetch: refetchTracking,
  } = useQuery({
    queryKey: ['admin', 'shipments', 'tracking', shipmentId],
    queryFn: () => getTrackingHistory(shipmentId),
    ...operationalQueryPolicy,
    refetchInterval: isTerminal(shipment?.status) ? false : OPERATIONAL_POLL_INTERVAL_MS,
    enabled: !!shipment && activeTab === 'tracking',
  });

  const {
    data: shipperMessages,
    isLoading: isMessagesLoading,
    isError: isMessagesError,
    refetch: refetchMessages,
  } = useQuery({
    queryKey: ['admin', 'shipments', 'shipper-messages', shipmentId],
    queryFn: () => listShipperMessages(shipmentId),
    ...operationalQueryPolicy,
    enabled: SHIPPER_ZERO_CALLS && !!shipment && activeTab === 'messages',
  });

  const {
    data: moneySummary,
    isLoading: isMoneyLoading,
    isError: isMoneyError,
    refetch: refetchMoney,
  } = useQuery({
    queryKey: ['payments', 'shipments', shipmentId],
    queryFn: () => getOrderMoneySummary(shipmentId),
    enabled: Number.isFinite(shipmentId) && activeTab === 'payments',
    ...operationalQueryPolicy,
  });

  // One cached identity/terms read for this order, never one read per payment row.
  const shipperPayments = useQuery({
    queryKey: ['admin', 'users', 'detail', shipment?.shipperId],
    queryFn: () => getUserById(shipment!.shipperId),
    enabled: shipment != null && activeTab === 'payments',
    staleTime: 60_000,
  });
  const shipperProfile = shipperPayments.data?.profile;

  const invalidateDetails = [
    ['admin', 'shipments', 'detail', shipmentId],
    ['admin', 'shipments', 'bids', shipmentId],
    ['admin', 'shipments', 'documents', shipmentId],
    ['admin', 'shipments', 'tracking', shipmentId],
    ['payments', 'shipments', shipmentId],
    ['admin', 'dashboard'],
  ];

  const cancelMut = useMutationHook(() => cancelShipment(shipmentId), {
    successMessage: 'Shipment has been successfully cancelled.',
    invalidateKeys: invalidateDetails,
    errorMessage: actionFailureMessage,
    onSuccess: () => setConfirmAction(null),
    onError: (err) => setDialogError(actionFailureMessage(err)),
  });

  const completeMut = useMutationHook(() => completeShipment(shipmentId), {
    successMessage: 'Shipment marked as completed.',
    invalidateKeys: invalidateDetails,
    errorMessage: actionFailureMessage,
    onSuccess: () => setConfirmAction(null),
    onError: (err) => setDialogError(actionFailureMessage(err)),
  });

  // Stops (dnm-work#165): the shipment status is never touched here (R53); every change refreshes
  // the detail, the stop history, the Stopped trucks list and the dashboard badge.
  const [stopSheet, setStopSheet] = useState<'open' | 'edit' | null>(null);
  const [stopError, setStopError] = useState<string | null>(null);
  const stopInvalidate = [...invalidateDetails, [...stopsQueryKey(shipmentId)], ...STOP_INVALIDATE_KEYS];
  const openStopMut = useMutationHook((body: OpenStopRequest) => openStopApi(shipmentId, body), {
    successMessage: t('stops.toast.opened'),
    invalidateKeys: stopInvalidate,
    errorMessage: actionFailureMessage,
    onSuccess: () => {
      setStopError(null);
      setStopSheet(null);
    },
    onError: (err) => setStopError(actionFailureMessage(err)),
  });
  const editStopMut = useMutationHook(
    ({ stopId, body }: { stopId: number; body: EditStopRequest }) => editStopApi(shipmentId, stopId, body),
    {
      successMessage: t('stops.toast.edited'),
      invalidateKeys: stopInvalidate,
      errorMessage: actionFailureMessage,
      onSuccess: () => {
        setStopError(null);
        setStopSheet(null);
      },
      onError: (err) => setStopError(actionFailureMessage(err)),
    },
  );
  const movingAgainMut = useMutationHook((stopId: number) => closeStopApi(shipmentId, stopId), {
    successMessage: t('stops.toast.closed'),
    invalidateKeys: stopInvalidate,
    errorMessage: actionFailureMessage,
    onSuccess: () => setConfirmAction(null),
    onError: (err) => setDialogError(actionFailureMessage(err)),
  });

  const repostMut = useMutationHook(() => repostShipment(shipmentId), {
    successMessage: 'Shipment successfully reposted to the marketplace.',
    invalidateKeys: invalidateDetails,
    errorMessage: actionFailureMessage,
    onSuccess: () => setConfirmAction(null),
    onError: (err) => setDialogError(actionFailureMessage(err)),
  });

  // Reverse award (#230): sends the chosen reason and note as the request body.
  const reverseAwardMessage = (err: unknown) =>
    (err instanceof DnmRequestFailure && reverseAwardReasonError(err.code)) ||
    actionFailureMessage(err);
  const reverseAwardMut = useMutationHook(
    (choice: ReverseAwardChoice) => reverseAward(shipmentId, choice),
    {
      successMessage: 'Award reversed. Shipment reopened for bidding under a new cycle.',
      invalidateKeys: invalidateDetails,
      errorMessage: reverseAwardMessage,
      onSuccess: () => setConfirmAction(null),
      onError: (err) => setDialogError(reverseAwardMessage(err)),
    },
  );

  // Record delay (#294): the sheet keeps its fields on a refusal and shows the Contract copy.
  const delayMessage = (err: unknown) => delayFailureCopy(err) ?? actionFailureMessage(err);
  const recordDelayMut = useMutationHook(
    (body: RecordDelayRequest) => recordShipmentDelay(shipmentId, body),
    {
      successMessage: 'Delay recorded. The shipper gets the WhatsApp message.',
      invalidateKeys: [...invalidateDetails, ['admin', 'shipments', 'shipper-messages', shipmentId]],
      errorMessage: delayMessage,
      onSuccess: () => {
        setDelayError(null);
        setDelaySheetOpen(false);
      },
      onError: (err) => setDelayError(delayMessage(err)),
    },
  );

  const [recomputeFailed, setRecomputeFailed] = useState(false);
  const recomputeDistanceMut = useMutationHook(() => recomputeShipmentDistance(shipmentId), {
    errorMessage: "Couldn't recalculate the distance. Try again.",
    invalidateKeys: invalidateDetails,
    onSuccess: (data) => {
      setConfirmAction(null);
      setRecomputeFailed(false);
      if (data.distanceIsEstimated === true) {
        toast.info('Road distance not available yet. Try again later.');
      } else {
        toast.success('Distance updated');
      }
    },
    onError: () => {
      setConfirmAction(null);
      setRecomputeFailed(true);
    },
  });
  const recomputeDistance = () => {
    if (recomputeDistanceMut.isLoading) return;
    setRecomputeFailed(false);
    recomputeDistanceMut.mutate(undefined);
  };

  const acceptBidMut = useMutationHook((bidId: number) => acceptBid(bidId), {
    successMessage: (data) => `Bid #${data.id} accepted! Shipment awarded.`,
    errorMessage: actionFailureMessage,
    invalidateKeys: invalidateDetails,
    onSuccess: () => {
      setConfirmAction(null);
      setSelectedBidId(null);
    },
    onError: (err) => setDialogError(actionFailureMessage(err)),
  });

  if (isShipmentLoading) return <LoadingState label={t('shipments.shipmentDetail.loading')} />;
  if (!shipment)
    return (
      <ErrorState
        title={t('shipments.shipmentDetail.loadError')}
        activeOperation
        onRetry={() => refetchShipment()}
      />
    );

  // Unknown (null) counts as maybe-estimated, so staff can also fill in a missing distance.
  const canRecomputeDistance =
    shipment.distanceIsEstimated !== false &&
    (shipment.status === 'DRAFT' || shipment.status === 'POSTED');

  const tabStates: Partial<
    Record<Tab, { loading: boolean; failed: boolean; hasData: boolean; retry: () => unknown }>
  > = {
    bids: {
      loading: isBidsLoading,
      failed: isBidsError,
      hasData: bids !== undefined,
      retry: () => refetchBids(),
    },
    interests: {
      loading: isInterestsLoading,
      failed: isInterestsError,
      hasData: interestsData !== undefined,
      retry: () => refetchInterests(),
    },
    documents: {
      loading: isDocumentsLoading,
      failed: isDocumentsError,
      hasData: documents !== undefined,
      retry: () => refetchDocuments(),
    },
    tracking: {
      loading: isTrackingLoading,
      failed: isTrackingError,
      hasData: tracking !== undefined,
      retry: () => refetchTracking(),
    },
    messages: {
      loading: isMessagesLoading,
      failed: isMessagesError,
      hasData: shipperMessages !== undefined,
      retry: () => refetchMessages(),
    },
    payments: {
      loading: isMoneyLoading,
      failed: isMoneyError,
      hasData: moneySummary !== undefined,
      retry: () => refetchMoney(),
    },
  };
  const tabState = tabStates[activeTab];
  const canRenderTab = !tabState || tabState.hasData || (!tabState.loading && !tabState.failed);

  const statusInfo = shipmentStatusColor(shipment.status);

  return (
    <div className={styles.page}>
      {isShipmentError && <StaleBanner onRetry={() => refetchShipment()} />}
      {/* Detail Header */}
      <div className={styles.header}>
        <Link to={getBackPath()} state={stateRestore} className={styles.backBtn}>
          {getBackLabel()}
        </Link>
        <div className={styles.headerRow}>
          <div>
            <h2 className={styles.title}>
              {t('shipments.shipmentDetail.header.shipment')} <span className={styles.mono}>#{shipment.id}</span>
              {shipment.currentPostingCycle != null && (
                <span className={styles.cycleBadge}>{t('shipments.shipmentDetail.header.cycle', { cycle: shipment.currentPostingCycle })}</span>
              )}
            </h2>
            <div className={styles.routeText}>
              {formatRoute(shipment, { long: true })}
              {formatDistance(shipment.distanceKm) && (
                <span className={styles.routeMeta}>
                  · {formatDistance(shipment.distanceKm)}
                  <ApproxMarker estimated={shipment.distanceIsEstimated} />
                  {formatDuration(shipment.estimatedDurationMinutes) && (
                    <span> ({formatDuration(shipment.estimatedDurationMinutes)})</span>
                  )}
                </span>
              )}
            </div>
            {shipment.shipmentUuid && (
              <div className={styles.uuidBadge}>
                <span>{t('shipments.shipmentDetail.header.uuid', { uuid: shipment.shipmentUuid })}</span>
                <button
                  type="button"
                  className={styles.copyBtn}
                  onClick={() => {
                    navigator.clipboard.writeText(shipment.shipmentUuid!);
                    toast.success('Copied UUID to clipboard');
                  }}
                  title={t('shipments.shipmentDetail.header.copyUuid')}
                >
                  {t('shipments.shipmentDetail.header.copy')}
                </button>
              </div>
            )}
          </div>
          <div className={styles.headerRight}>
            <StatusPill bg={statusInfo.bg} text={statusInfo.text} label={shipmentStatusLabel(shipment.status)} title={shipment.status} />
            {/* #300: per-order profit, demo world and managers only (P9). */}
            {isDemo() && isManager && shipment.profit && (
              <span data-testid="shipment-profit">
                <ProfitCell profit={shipment.profit} />
              </span>
            )}
            <div className={styles.controls}>
              {shipment.status === 'POSTED' && (
                <button className={styles.btnControl} onClick={() => setIsEditShipmentOpen(true)}>
                  {t('shipments.shipmentDetail.header.editShipment')}
                </button>
              )}
              {canEditPickup(shipment.status) && (
                <button className={styles.btnControl} onClick={() => setEditStop('PICKUP')}>
                  {t('shipments.shipmentDetail.header.editPickup')}
                </button>
              )}
              {canEditDrop(shipment.status) && (
                <button className={styles.btnControl} onClick={() => setEditStop('DROP')}>
                  {t('shipments.shipmentDetail.header.editDrop')}
                </button>
              )}
              {shipment.status === 'AWARDED' && (
                <button
                  className={`${styles.btnControl} ${styles.btnUnaward}`}
                  onClick={() => setConfirmAction('reverseAward')}
                >
                  {t('shipments.shipmentDetail.header.unaward')}
                </button>
              )}
              {shipment.status === 'AWARDED' && !shipment.assignment && (
                <button
                  className={`${styles.btnControl} ${styles.btnAssign}`}
                  onClick={() => handleOpenAssignDriver('change_driver')}
                >
                  {t('shipments.shipmentDetail.actions.assignDriver')}
                </button>
              )}
              {shipment.assignment && !isDeliveredOrTerminal(shipment.status) && (
                <button
                  className={styles.btnControl}
                  onClick={() => handleOpenAssignDriver('edit_details')}
                  title={t('shipments.shipmentDetail.actions.manageDriverHint')}
                >
                  {t('shipments.shipmentDetail.actions.manageDriver')}
                </button>
              )}
              {shipment.status === 'ASSIGNED' && (
                <button
                  className={`${styles.btnControl} ${styles.btnAssign}`}
                  onClick={() => setAdvanceStatus('EN_ROUTE_PICKUP')}
                >
                  {t('shipments.shipmentDetail.actions.markHeadingToPickup')}
                </button>
              )}
              {shipment.status === 'EN_ROUTE_PICKUP' && (
                <button
                  className={`${styles.btnControl} ${styles.btnAssign}`}
                  onClick={() => setAdvanceStatus('AT_PICKUP')}
                >
                  {t('shipments.shipmentDetail.actions.markAtPickup')}
                </button>
              )}
              {shipment.status === 'AT_PICKUP' && (
                <button
                  className={`${styles.btnControl} ${styles.btnAssign}`}
                  onClick={() => setAdvanceStatus('LOADED')}
                >
                  {t('shipments.shipmentDetail.actions.markLoaded')}
                </button>
              )}
              {shipment.status === 'LOADED' && (
                <button
                  className={`${styles.btnControl} ${styles.btnAssign}`}
                  onClick={() => setAdvanceStatus('IN_TRANSIT')}
                >
                  {t('shipments.shipmentDetail.actions.markInTransit')}
                </button>
              )}
              {shipment.status === 'IN_TRANSIT' && SHIPPER_ZERO_CALLS && (
                <Button
                  variant="secondary"
                  className={styles.iconButton}
                  onClick={() => {
                    setDelayError(null);
                    setDelaySheetOpen(true);
                  }}
                >
                  <StatusIcon name="schedule" size={16} />
                  {t('shipments.shipmentDetail.actions.recordDelay')}
                </Button>
              )}
              {shipment.status === 'IN_TRANSIT' && (
                <>
                  <button
                    className={styles.btnControl}
                    onClick={() => setIsInTransitLocationOpen(true)}
                  >
                    {t('shipments.shipmentDetail.actions.updateLocation')}
                  </button>
                  <button
                    className={`${styles.btnControl} ${styles.btnAssign}`}
                    onClick={() => setAdvanceStatus('AT_DROP')}
                  >
                    {t('shipments.shipmentDetail.actions.markAtDrop')}
                  </button>
                </>
              )}
              {shipment.status === 'AT_DROP' && (
                <button
                  className={`${styles.btnControl} ${styles.btnAssign}`}
                  onClick={() => setAdvanceStatus('DELIVERED')}
                >
                  {t('shipments.shipmentDetail.actions.markDelivered')}
                </button>
              )}
              {shipment.status === 'DELIVERED' && (
                <button
                  className={`${styles.btnControl} ${styles.btnComplete}`}
                  onClick={() => setAdvanceStatus('COMPLETED')}
                >
                  {t('shipments.shipmentDetail.actions.complete')}
                </button>
              )}
              {shipment.status !== 'DELIVERED' &&
                shipment.status !== 'COMPLETED' &&
                shipment.status !== 'CANCELLED' &&
                shipment.status !== 'EXPIRED' && (
                  <button
                    className={`${styles.btnControl} ${styles.btnCancel}`}
                    onClick={() => setConfirmAction('cancel')}
                  >
                    {t('shipments.shipmentDetail.actions.cancelShipment')}
                  </button>
                )}
              {STOPPABLE_STATUSES.includes(shipment.status) && !shipment.openStop && (
                <Button
                  variant="secondary"
                  className={styles.iconButton}
                  onClick={() => {
                    setStopError(null);
                    setStopSheet('open');
                  }}
                >
                  <StatusIcon name="warning" size={16} />
                  {t('stops.actions.truckStopped')}
                </Button>
              )}
              {shipment.openStop && (
                <>
                  <Button variant="primary" onClick={() => setConfirmAction('movingAgain')}>
                    {t('stops.actions.movingAgain')}
                  </Button>
                  <Button
                    variant="secondary"
                    className={styles.iconButton}
                    onClick={() => {
                      setStopError(null);
                      setStopSheet('edit');
                    }}
                  >
                    <StatusIcon name="edit" size={16} />
                    {t('stops.actions.editStop')}
                  </Button>
                </>
              )}
              {(shipment.status === 'CANCELLED' || shipment.status === 'EXPIRED') && (
                <button
                  className={`${styles.btnControl} ${styles.btnRepost}`}
                  onClick={() => setConfirmAction('repost')}
                >
                  {t('shipments.shipmentDetail.actions.repost')}
                </button>
              )}
              <button
                id="view-notification-history-btn"
                type="button"
                className={`${styles.btnControl} ${styles.btnNotifHistory}`}
                onClick={() => setNotificationDrawerOpen(true)}
                title={t('shipments.shipmentDetail.actions.notificationHistoryHint')}
              >
                {t('shipments.shipmentDetail.actions.notificationHistory')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Operational Milestone Stepper */}
      <ShipmentMilestoneStepper status={shipment.status} openStop={shipment.openStop} />

      <DetentionCard detention={shipment.detention} />

      {/* Awarded but unassigned banner */}
      {shipment.status === 'AWARDED' && !shipment.assignment && (
        <div className={styles.awardedBanner}>
          <div>
            <strong>{t('shipments.shipmentDetail.awardedBanner.title')}</strong> {t('shipments.shipmentDetail.awardedBanner.text')}
          </div>
          <button
            className={`${styles.btnControl} ${styles.btnAssign}`}
            style={{ padding: '5px 12px', fontSize: 12 }}
            onClick={() => handleOpenAssignDriver('change_driver')}
          >
            {t('shipments.shipmentDetail.awardedBanner.assignDriver')}
          </button>
        </div>
      )}

      {/* Ops ownership: owner, claim / release / reassign, history */}
      <OwnershipPanel shipment={shipment} />

      {/* Tabs list */}
      <div className={styles.tabs}>
        {(
          [
            'overview',
            'interests',
            'bids',
            'payments',
            'documents',
            'tracking',
            'assignment',
            ...(SHIPPER_ZERO_CALLS ? ['messages'] : []),
          ] as Tab[]
        ).map((tab) => (
          <button
            key={tab}
            className={activeTab === tab ? `${styles.tabBtn} ${styles.tabActive}` : styles.tabBtn}
            onClick={() => setActiveTab(tab)}
          >
            {tab === 'interests'
              ? `Interests (${distinctInterestsCount})`
              : tab === 'assignment'
                ? 'Driver & Trucker'
                : tab === 'bids'
                  ? 'Bid(Rate)'
                  : tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
        <div className={styles.tabsRefresh}>
          <RefreshButton
            title={`Refresh ${activeTab}`}
            queryKeys={[
              ['admin', 'shipments', 'detail', shipmentId],
              ...(activeTab === 'overview' ? [['admin', 'service-lanes']] : []),
              ...(activeTab === 'interests'
                ? [['admin', 'interests', 'shipment', shipment?.shipmentUuid]]
                : []),
              ...(activeTab === 'bids' ? [['admin', 'shipments', 'bids', shipmentId]] : []),
              ...(activeTab === 'documents'
                ? [['admin', 'shipments', 'documents', shipmentId]]
                : []),
              ...(activeTab === 'tracking' ? [['admin', 'shipments', 'tracking', shipmentId]] : []),
              ...(activeTab === 'payments' ? [['payments', 'shipments', shipmentId]] : []),
              ...(activeTab === 'messages'
                ? [['admin', 'shipments', 'shipper-messages', shipmentId]]
                : []),
            ]}
          />
        </div>
      </div>

      {/* Tab content */}
      <div className={styles.tabContent}>
        {tabState?.loading && !tabState.hasData && <LoadingState label={`Loading ${activeTab}…`} />}
        {tabState?.failed &&
          (tabState.hasData ? (
            <StaleBanner onRetry={tabState.retry} />
          ) : (
            <ErrorState
              title={`We couldn’t load ${activeTab}`}
              activeOperation
              onRetry={tabState.retry}
            />
          ))}
        {activeTab === 'messages' && shipperMessages && canRenderTab && (
          <ShipperMessagesTab messages={shipperMessages} />
        )}
        {activeTab === 'overview' && (
          <div className={styles.grid}>
            {shipment.delay && <DelayCard delay={shipment.delay} />}
            <div className={styles.card}>
              <h3 className={styles.cardTitle}>{t('shipments.shipmentDetail.cards.shipmentDetails')}</h3>

              {/* Shipper row (design: clickable blue name + mono phone + chevron,
                  above a divider) — links directly to full profile */}
              <Link
                to={`/users/${shipment.shipperId}`}
                state={{ from: `/shipments/${shipment.id}` }}
                className={`${styles.shipperRow} ${styles.shipperRowTop}`}
                title={t('shipments.shipmentDetail.overview.viewShipperProfile')}
              >
                <div className={styles.shipperInfo}>
                  <span className={styles.label}>{t('shipments.shipmentDetail.labels.shipper')}</span>
                  <div className={styles.shipperName}>
                    {shipment.shipperName ?? `Shipper #${shipment.shipperId}`}
                  </div>
                  {shipment.shipperPhone && (
                    <div className={styles.shipperPhone}>{shipment.shipperPhone}</div>
                  )}
                  {shipment.shipperEmail && (
                    <div className={styles.shipperEmail}>{shipment.shipperEmail}</div>
                  )}
                </div>
                <span className={styles.shipperChevron}>›</span>
              </Link>

              <div className={styles.detailRow}>
                <span className={styles.label}>{t('shipments.shipmentDetail.labels.originAddress')}</span>
                <span className={styles.val}>
                  {shipment.originAddress || '—'}
                  {shipment.originAddress && (
                    <a
                      className={styles.mapLink}
                      href={buildMapsUrl(
                        shipment.pickupAddress,
                        shipment.originAddress,
                        shipment.originCity,
                      )}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t('shipments.shipmentDetail.overview.openInMaps')}
                    </a>
                  )}
                </span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.label}>{t('shipments.shipmentDetail.labels.destinationAddress')}</span>
                <span className={styles.val}>
                  {shipment.destinationAddress || '—'}
                  {shipment.destinationAddress && (
                    <a
                      className={styles.mapLink}
                      href={buildMapsUrl(
                        shipment.dropAddress,
                        shipment.destinationAddress,
                        shipment.destinationCity,
                      )}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t('shipments.shipmentDetail.overview.openInMaps')}
                    </a>
                  )}
                </span>
              </div>
              {(formatDistance(shipment.distanceKm) || canRecomputeDistance) && (
                <div className={styles.detailRow}>
                  <span className={styles.label}>{t('shipments.shipmentDetail.labels.routeDistance')}</span>
                  <span className={styles.val}>
                    {formatDistance(shipment.distanceKm) ?? '—'}
                    <ApproxMarker estimated={shipment.distanceIsEstimated} />
                    {formatDuration(shipment.estimatedDurationMinutes) && (
                      <span style={{ color: 'var(--muted)', marginLeft: 6 }}>
                        ({formatDuration(shipment.estimatedDurationMinutes)})
                      </span>
                    )}
                    {canRecomputeDistance && (
                      <button
                        type="button"
                        className={styles.recomputeLink}
                        disabled={recomputeDistanceMut.isLoading}
                        onClick={() =>
                          shipment.status === 'POSTED'
                            ? setConfirmAction('recomputeDistance')
                            : recomputeDistance()
                        }
                      >
                        {recomputeDistanceMut.isLoading ? 'Recalculating…' : 'Recalculate distance'}
                      </button>
                    )}
                    {recomputeFailed && (
                      <span role="alert" className={styles.recomputeError}>
                        {t('shipments.shipmentDetail.overview.recomputeFailed')}
                        <button
                          type="button"
                          className={styles.recomputeLink}
                          disabled={recomputeDistanceMut.isLoading}
                          onClick={recomputeDistance}
                        >
                          {t('shipments.shipmentDetail.overview.retry')}
                        </button>
                      </span>
                    )}
                  </span>
                </div>
              )}
              <div className={styles.detailRow}>
                <span className={styles.label}>{t('shipments.shipmentDetail.labels.pickupDate')}</span>
                <span className={styles.val}>
                  {fmtDate(shipment.pickupDate ? shipment.pickupDate.toString() : null)}
                </span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.label}>{t('shipments.shipmentDetail.labels.deliveryDate')}</span>
                <span className={styles.val}>
                  {fmtDate(shipment.deliveryDate ? shipment.deliveryDate.toString() : null)}
                </span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.label}>{t('shipments.shipmentDetail.labels.description')}</span>
                <span className={styles.val}>{shipment.cargoDescription || '—'}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.label}>{t('shipments.shipmentDetail.labels.weight')}</span>
                <span className={styles.val}>{tons(shipment.weightKg)}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.label}>{t('shipments.shipmentDetail.labels.volume')}</span>
                <span className={styles.val}>
                  {shipment.volumeCbm ? `${shipment.volumeCbm} CBM` : '—'}
                </span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.label}>{t('shipments.shipmentDetail.labels.truckType')}</span>
                <span className={styles.val}>
                  {(() => {
                    // #249 display rule (truckTypeLabel); the icon only when the category is known.
                    const label = truckTypeLabel(shipment);
                    const match =
                      !shipment.vehicleTypeOther?.trim() && shipment.vehicleTypeRequired
                        ? findTruckType(shipment.vehicleTypeRequired)
                        : undefined;
                    if (!match) return label;
                    return (
                      <span className={styles.truckVal}>
                        <TruckIcon categoryId={match.category.id} size={18} />
                        {label}
                      </span>
                    );
                  })()}
                </span>
              </div>
            </div>

            {(shipment.status === 'DRAFT' || shipment.status === 'POSTED') && (
              <FleetManagerSuggestions
                shipment={shipment}
                onPlaceBid={(fleetManagerId) => {
                  setBidPrefillId(fleetManagerId);
                  setBidModal('create');
                }}
              />
            )}

            {/* Assigned Carrier & Driver card */}
            <div className={styles.card}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 14,
                }}
              >
                <h3 className={styles.cardTitle} style={{ margin: 0 }}>
                  {t('shipments.shipmentDetail.cards.assignedCarrierDriver')}
                </h3>
                {shipment.status === 'AWARDED' && !shipment.assignment && (
                  <button
                    className={`${styles.btnControl} ${styles.btnAssign}`}
                    style={{ padding: '4px 10px', fontSize: 12 }}
                    onClick={() => handleOpenAssignDriver('change_driver')}
                  >
                    {t('shipments.shipmentDetail.awardedBanner.assignDriver')}
                  </button>
                )}
                {shipment.assignment && !isDeliveredOrTerminal(shipment.status) && (
                  <button
                    className={styles.btnControl}
                    style={{ padding: '4px 10px', fontSize: 12 }}
                    onClick={() => handleOpenAssignDriver('edit_details')}
                    title={t('shipments.shipmentDetail.assignment.manageHint')}
                  >
                    {t('shipments.shipmentDetail.actions.manageDriver')}
                  </button>
                )}
              </div>

              {shipment.assignment ? (
                <>
                  <div className={styles.detailRow}>
                    <span className={styles.label}>{t('shipments.shipmentDetail.labels.driverName')}</span>
                    <span className={styles.val} style={{ fontWeight: 600 }}>
                      {shipment.assignment.driverName ??
                        (shipment.driverId ? `Driver #${shipment.driverId}` : '—')}
                    </span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.label}>{t('shipments.shipmentDetail.labels.driverPhone')}</span>
                    <span className={styles.val} style={{ fontFamily: 'var(--font-mono)' }}>
                      {shipment.assignment.driverPhone ? (
                        <a
                          href={`tel:${shipment.assignment.driverPhone}`}
                          style={{ color: 'var(--accent)', textDecoration: 'none' }}
                        >
                          📞 {shipment.assignment.driverPhone}
                        </a>
                      ) : (
                        '—'
                      )}
                    </span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.label}>{t('shipments.shipmentDetail.labels.vehicleNumber')}</span>
                    <span
                      className={styles.val}
                      style={{ fontWeight: 600, fontFamily: 'var(--font-mono)' }}
                    >
                      {shipment.assignment.vehicleRegistrationNumber ?? '—'}
                    </span>
                  </div>
                  {shipment.assignment.vehicleId && (
                    <div className={styles.detailRow}>
                      <span className={styles.label}>{t('shipments.shipmentDetail.labels.vehicleId')}</span>
                      <span className={`${styles.val} ${styles.mono}`}>
                        #{shipment.assignment.vehicleId}
                      </span>
                    </div>
                  )}
                  {shipment.assignment.fleetManagerName && (
                    <div className={styles.detailRow}>
                      <span className={styles.label}>{t('shipments.shipmentDetail.labels.fleetManager')}</span>
                      <span className={styles.val}>
                        {shipment.assignment.fleetManagerName}
                        {shipment.assignment.fleetManagerPhone && (
                          <span
                            style={{
                              marginLeft: 8,
                              color: 'var(--muted)',
                              fontFamily: 'var(--font-mono)',
                            }}
                          >
                            ({shipment.assignment.fleetManagerPhone})
                          </span>
                        )}
                      </span>
                    </div>
                  )}
                  <div className={styles.detailRow}>
                    <span className={styles.label}>{t('shipments.shipmentDetail.labels.assignmentStatus')}</span>
                    <span className={styles.val}>
                      <span
                        style={{
                          padding: '2px 8px',
                          borderRadius: 10,
                          fontSize: 11.5,
                          fontWeight: 600,
                          background:
                            shipment.assignment.status === 'ACCEPTED' ||
                            shipment.assignment.status === 'COMPLETED'
                              ? 'var(--status-green-bg)'
                              : 'var(--bg)',
                          color:
                            shipment.assignment.status === 'ACCEPTED' ||
                            shipment.assignment.status === 'COMPLETED'
                              ? 'var(--status-green-text)'
                              : 'var(--ink)',
                        }}
                      >
                        {codeLabel('assignmentStatus', shipment.assignment.status)}
                      </span>
                    </span>
                  </div>
                </>
              ) : (
                <div style={{ color: 'var(--muted)', fontSize: 13, padding: '8px 0' }}>
                  {t('shipments.shipmentDetail.assignment.none')}{' '}
                  {shipment.status === 'AWARDED' && (
                    <span
                      style={{ color: 'var(--accent)', cursor: 'pointer', fontWeight: 600 }}
                      onClick={() => setIsAssignDriverOpen(true)}
                    >
                      {t('shipments.shipmentDetail.assignment.assignLink')}
                    </span>
                  )}
                </div>
              )}
              <ParticipantKycSection shipment={shipment} from={`/shipments/${shipment.id}`} />
            </div>

            <div className={styles.card}>
              <h3 className={styles.cardTitle}>{t('shipments.shipmentDetail.cards.pricingBidding')}</h3>
              <div className={styles.detailRow}>
                <span className={styles.label}>{t('shipments.shipmentDetail.labels.shipperBudgetOriginal')}</span>
                <span className={styles.val}>{curr(shipment.offeredPrice)}</span>
              </div>
              {/* #298: where the shipper price came from; every ops role sees it (demo world only until the build issue). */}
              {shipment.priceSource != null && (
                <div className={styles.detailRow}>
                  <span className={styles.label}>{t('shipments.shipmentDetail.labels.rateSource')}</span>
                  <span className={styles.val}>
                    <RateSourcePill source={shipment.priceSource} />
                  </span>
                </div>
              )}
              {/* #288, R36: the per-order price partners see; the global margin % is only a hint in the dialog. */}
              <div className={styles.detailRow}>
                <span className={styles.label}>{t('shipments.shipmentDetail.labels.partnerPrice')}</span>
                <span className={`${styles.val} ${styles.priceWithActions}`}>
                  {shipment.partnerOfferPrice != null ? (
                    curr(shipment.partnerOfferPrice)
                  ) : (
                    <span className={styles.notSet}>{t('shipments.shipmentDetail.pricing.notSet')}</span>
                  )}
                  {partnerPriceEditable(shipment.status) && (
                    <Button
                      variant="secondary"
                      size="sm"
                      aria-label={
                        shipment.partnerOfferPrice != null ? 'Edit partner price' : 'Set partner price'
                      }
                      onClick={() => setPartnerPriceOpen(true)}
                    >
                      {shipment.partnerOfferPrice != null ? 'Edit' : 'Set'}
                    </Button>
                  )}
                </span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.label}>{t('shipments.shipmentDetail.labels.finalPriceCarrierGets')}</span>
                <span className={styles.val}>{curr(shipment.finalPrice)}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.label}>
                  {t('shipments.shipmentDetail.pricing.shipperFacingFinal')}
                  {shipment.shipperFreightFixed ? '' : ` (${marginLabel(shipment.shipperMarginPct, '+')})`}
                </span>
                <span className={`${styles.val} ${styles.priceWithActions}`}>
                  {curr(shipment.shipperFacingFinalPrice)}
                  {shipment.shipperFreightFixed && (
                    <StatusPill
                      bg="var(--status-gray-bg)"
                      text="var(--status-gray-text)"
                      label={t('shipments.shipmentDetail.pricing.fixed')}
                      title={t('shipments.shipmentDetail.pricing.fixedHint')}
                      icon={<StatusIcon name="edit" />}
                    />
                  )}
                  {shipment.winningBidId != null &&
                    shipment.shipperFacingFinalPrice != null &&
                    shipment.shipperFreightEditable != null && (
                      <span
                        title={shipment.shipperFreightEditable ? undefined : SHIPPER_FREIGHT_LOCKED_HINT}
                      >
                        <Button
                          variant="secondary"
                          size="sm"
                          aria-label={t('shipments.shipmentDetail.pricing.editShipperFinal')}
                          disabled={!shipment.shipperFreightEditable}
                          onClick={() => setShipperFreightOpen(true)}
                        >
                          {t('shipments.shipmentDetail.pricing.edit')}
                        </Button>
                      </span>
                    )}
                </span>
              </div>
              {shipment.winningBidId != null && shipment.shipperFreightEditable === false && (
                <p className={styles.lockedHint}>{SHIPPER_FREIGHT_LOCKED_HINT}</p>
              )}
              {shipment.shipperMarginAmount != null && (
                <div className={styles.detailRow}>
                  <span className={styles.label}>{t('shipments.shipmentDetail.labels.margin')}</span>
                  <span className={`${styles.val} ${styles.priceWithActions}`}>
                    {shipment.negativeMargin && (
                      <StatusPill
                        bg="var(--status-red-bg)"
                        text="var(--status-red-text)"
                        label={t('shipments.shipmentDetail.pricing.loss')}
                        icon={<StatusIcon name="close" />}
                      />
                    )}
                    {serverMarginText(shipment.shipperMarginAmount, shipment.shipperEffectiveMarginPct)}
                  </span>
                </div>
              )}
              <div className={styles.detailRow}>
                <span className={styles.label}>{t('shipments.shipmentDetail.labels.marginSnapshot')}</span>
                <span className={styles.val}>
                  {t('shipments.shipmentDetail.pricing.marginSnapshot', {
                    carrier: marginLabel(shipment.carrierMarginPct, '−'),
                    shipper: marginLabel(shipment.shipperMarginPct, '+'),
                  })}
                </span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.label}>{t('shipments.shipmentDetail.labels.winningBidId')}</span>
                <span className={`${styles.val} ${styles.mono}`}>
                  {shipment.winningBidId ? `#${shipment.winningBidId}` : '—'}
                </span>
              </div>
            </div>
          </div>
        )}
        {activeTab === 'payments' && canRenderTab && moneySummary && (
          <OrderPaymentsPanel
            key={shipmentId}
            shipper={{
              name:
                typeof shipperProfile?.companyName === 'string' && shipperProfile.companyName.trim()
                  ? shipperProfile.companyName
                  : shipment.shipperName,
              gstin: shipperPayments.isSuccess
                ? typeof shipperProfile?.gstin === 'string'
                  ? shipperProfile.gstin
                  : null
                : undefined,
              creditDays: shipperPayments.isSuccess
                ? typeof shipperProfile?.paymentTermsDays === 'number'
                  ? shipperProfile.paymentTermsDays
                  : null
                : undefined,
            }}
            shipperIdentityLoading={
              shipperPayments.isLoading && shipperPayments.fetchStatus !== 'idle'
            }
            shipperIdentityError={shipperPayments.isError}
            onRetryShipperIdentity={() => void shipperPayments.refetch()}
            vehicleNumber={shipment.assignment?.vehicleRegistrationNumber}
            partnerId={shipment.fleetManagerId}
            detention={shipment.detention}
            summary={moneySummary}
            isManager={isManager}
            onAddCharge={() => setIsAddChargeOpen(true)}
            actionPending={
              approveChargeMut.isPending || rejectChargeMut.isPending || voidChargeMut.isPending
            }
            actionError={chargeActionError}
            onReviewCharge={setReviewPartnerChargeId}
            onApproveCharge={async (chargeId) => {
              if (moneySummary?.charges.some(charge => charge.id === chargeId && charge.requestedByUserId != null)) { setReviewPartnerChargeId(chargeId); return; }
              setChargeActionError(null);
              try {
                await approveChargeMut.mutateAsync(chargeId);
                await refetchMoney();
              } catch (error) {
                setChargeActionError(mapPaymentError(error));
              }
            }}
            onRejectCharge={(chargeId) => {
              setChargeActionError(null);
              setChargeReason({ kind: 'reject', chargeId });
            }}
            onVoidCharge={(chargeId) => {
              setChargeActionError(null);
              setChargeReason({ kind: 'void', chargeId });
            }}
            onSetPartnerBill={() => setBillDialogOpen(true)}
          />
        )}

        {activeTab === 'interests' && canRenderTab && (
          <div className={styles.card}>
            <div className={styles.cardHeaderRow}>
              <h3 className={styles.cardTitle}>
                {t('shipments.shipmentDetail.interests.title', { n: distinctInterestsCount })}
              </h3>
            </div>
            {!interestsData || interestsData.items.length === 0 ? (
              <p className={styles.empty}>{t('shipments.shipmentDetail.empty.noVendorInterestsExpressedForThisShipment')}</p>
            ) : (
              <div className={styles.tableWrapper}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>{t('shipments.shipmentDetail.table.vendor')}</th>
                      <th>{t('shipments.shipmentDetail.table.status')}</th>
                      <th>{t('shipments.shipmentDetail.table.owner')}</th>
                      <th>{t('shipments.shipmentDetail.table.followUp')}</th>
                      <th>{t('shipments.shipmentDetail.table.vendorNote')}</th>
                      <th>{t('shipments.shipmentDetail.table.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {interestsData.items.map((interest) => {
                      const colors = interestStatusColor(interest.status);
                      return (
                        <tr key={interest.id}>
                          <td>
                            <Link to={`/users/${interest.vendorId}`} className={styles.detailLink}>
                              {interest.vendorName || `Partner #${interest.vendorId}`}
                            </Link>
                            <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                              {interest.vendorPhone || 'No phone'}
                            </div>
                          </td>
                          <td>
                            <StatusPill
                              label={codeLabel('interestStatus', interest.status)}
                              bg={colors.bg}
                              text={colors.text}
                            />
                          </td>
                          <td>{interest.ownerAdminName || 'Unassigned'}</td>
                          <td>{fmtDate(interest.nextFollowUpAt)}</td>
                          <td
                            style={{
                              maxWidth: 220,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {interest.vendorNote || '—'}
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: 6 }}>
                              <button
                                type="button"
                                className={`${styles.btnAction} ${styles.btnNotifHistory}`}
                                onClick={() => setSelectedInterestId(interest.id)}
                              >
                                {t('shipments.shipmentDetail.interests.manage')}
                              </button>
                              {interest.status !== 'CONVERTED_TO_BID' &&
                                interest.status !== 'CLOSED' &&
                                interest.status !== 'WITHDRAWN' && (
                                  <button
                                    type="button"
                                    className={`${styles.btnAction} ${styles.acceptBidBtn}`}
                                    onClick={() => setInterestToConvert(interest)}
                                  >
                                    {t('shipments.shipmentDetail.interests.convertToBid')}
                                  </button>
                                )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'bids' &&
          canRenderTab &&
          (() => {
            const currentCycle = shipment.currentPostingCycle ?? 1;
            const currentBids = (bids ?? []).filter((b) => (b.postingCycle ?? 1) === currentCycle);
            const priorBids = (bids ?? []).filter((b) => (b.postingCycle ?? 1) !== currentCycle);

            const renderBidsTable = (bidList: typeof bids, isPrior = false) => {
              if (!bidList || bidList.length === 0) {
                return <p className={styles.empty}>{t('shipments.shipmentDetail.empty.noBidsFound')}</p>;
              }
              return (
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>{t('shipments.shipmentDetail.table.bidId')}</th>
                      <th>{t('shipments.shipmentDetail.table.bidder')}</th>
                      <th>{t('shipments.shipmentDetail.table.cycle')}</th>
                      <th>{t('shipments.shipmentDetail.table.carrierBid')}</th>
                      <th>{t('shipments.shipmentDetail.table.shipperSees')}</th>
                      <th>{t('shipments.shipmentDetail.table.etaHours')}</th>
                      <th>{t('shipments.shipmentDetail.table.status')}</th>
                      <th>{t('shipments.shipmentDetail.table.placedAt')}</th>
                      {!isPrior && <th>{t('shipments.shipmentDetail.table.actions')}</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {bidList.map((b) => (
                      <tr
                        key={b.id}
                        onClick={() =>
                          navigate(`/users/${b.bidderId}`, {
                            state: { from: `/shipments/${shipmentId}` },
                          })
                        }
                        className={`${b.id === shipment.winningBidId ? styles.winningRow : ''} ${styles.clickableRow}`}
                        style={{ cursor: 'pointer' }}
                      >
                        <td className={styles.mono}>
                          #{b.id} {b.id === shipment.winningBidId && '🏆'}
                        </td>
                        <td>
                          {(() => {
                            const label = partyLabel({
                              company: b.bidderCompanyName,
                              name: b.bidderName,
                              phone: b.bidderPhone,
                              fallback: 'Unknown Bidder',
                            });
                            const meta = b.bidderCompanyName?.trim()
                              ? [label.secondary, `#${b.bidderId}`].filter(Boolean).join(' · ')
                              : `#${b.bidderId}${b.bidderPhone ? ` • ${b.bidderPhone}` : ''}`;
                            return (
                              <div className={styles.bidderCell}>
                                <span className={styles.bidderName} title={label.primary}>
                                  {label.primary}
                                </span>
                                <span className={styles.bidderMeta} title={meta}>
                                  {meta}
                                </span>
                              </div>
                            );
                          })()}
                        </td>
                        <td>
                          <span className={styles.mono}>{t('shipments.shipmentDetail.header.cycle', { cycle: b.postingCycle ?? 1 })}</span>
                          {isPrior && (
                            <span
                              className={styles.staleCycleBadge}
                              title={t('shipments.shipmentDetail.bids.priorHint')}
                            >
                              {t('shipments.shipmentDetail.bids.prior')}
                            </span>
                          )}
                        </td>
                        <td>{curr(b.amount)}</td>
                        <td>
                          {curr(b.shipperFacingAmount ?? b.amount)}
                          {(() => {
                            const meta = bidShipperMeta(b);
                            return (
                              meta && (
                                <span className={b.negativeMargin ? styles.lossMeta : styles.bidderMeta}>
                                  {' '}
                                  ({meta})
                                </span>
                              )
                            );
                          })()}
                        </td>
                        <td>{t('shipments.shipmentDetail.bids.etaHours', { hours: b.etaHours })}</td>
                        <td>
                          {(() => {
                            const pill = bidStatusDisplay(b.status);
                            const { reversalReasonCode, reversalNote } = b;
                            const reason =
                              b.status === 'AWARD_REVERSED' && reversalReasonCode
                                ? [reverseAwardReasonLabel(reversalReasonCode), reversalNote?.trim()]
                                    .filter(Boolean)
                                    .join(' · ')
                                : null;
                            return (
                              <>
                                <StatusPill
                                  bg={pill.bg}
                                  text={pill.text}
                                  label={pill.label}
                                  icon={pill.icon && <StatusIcon name={pill.icon} />}
                                  title={b.status}
                                />
                                {reason && (
                                  <span
                                    className={styles.reversalReason}
                                    title={reason}
                                    data-testid="reversal-reason"
                                  >
                                    {reason}
                                  </span>
                                )}
                              </>
                            );
                          })()}
                        </td>
                        <td>{fmtDate(b.createdAt)}</td>
                        {!isPrior && (
                          <td>
                            {(b.status === 'POSTED' || b.status === 'PENDING') &&
                              shipment.status === 'POSTED' &&
                              (b.postingCycle == null ||
                                shipment.currentPostingCycle == null ||
                                b.postingCycle === shipment.currentPostingCycle) && (
                                <>
                                  {(() => {
                                    // R18: award blocks on the shipper, then the winning bidder (no ops override).
                                    const blocker = bidAwardBlocker(shipment, b);
                                    const reason =
                                      blocker === 'SHIPPER'
                                        ? t('shipments.shipmentDetail.bids.shipperBlocksAward')
                                        : blocker === 'BIDDER'
                                          ? t('shipments.shipmentDetail.bids.bidderBlocksAward')
                                          : undefined;
                                    return (
                                      <>
                                        <button
                                          className={`${styles.btnAction} ${styles.acceptBidBtn}`}
                                          disabled={!!blocker}
                                          aria-disabled={blocker ? true : undefined}
                                          title={reason}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (blocker) return;
                                            setSelectedBidId(b.id);
                                            setConfirmAction('acceptBid');
                                          }}
                                        >
                                          {t('shipments.shipmentDetail.bids.acceptBid')}
                                        </button>
                                        {blocker === 'BIDDER' && (
                                          <Link
                                            to={`/users/${b.bidderId}`}
                                            state={{ from: `/shipments/${shipmentId}` }}
                                            className={styles.kycBlockLink}
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            {t('shipments.shipmentDetail.bids.bidderNotVerified')}
                                          </Link>
                                        )}
                                      </>
                                    );
                                  })()}
                                  <button
                                    className={styles.btnAction}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setBidModal(b);
                                    }}
                                  >
                                    {t('shipments.shipmentDetail.pricing.edit')}
                                  </button>
                                </>
                              )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              );
            };

            return (
              <div className={styles.card}>
                <div className={styles.cardHeaderRow}>
                  <h3 className={styles.cardTitle}>{t('shipments.shipmentDetail.bids.currentTitle', { cycle: currentCycle })}</h3>
                  {shipment.status === 'POSTED' && (
                    <button className={styles.addRowBtn} onClick={() => setBidModal('create')}>
                      {t('shipments.shipmentDetail.bids.addBid')}
                    </button>
                  )}
                </div>
                {/* #300: lane history while awarding (demo, managers only; silent on error). */}
                {shipment.status === 'POSTED' && (
                  <LaneHint originCity={shipment.originCity} destinationCity={shipment.destinationCity} />
                )}

                {(() => {
                  const shipperGate = gateFor(kycGate(shipment), 'SHIPPER');
                  if (shipperGate?.effect !== 'BLOCKS_AWARD' || currentBids.length === 0) return null;
                  const shipperId = shipperGate.entry.userId;
                  return (
                    <div className={styles.kycBanner}>
                      <Notice tone="danger" testId="bids-shipper-kyc-banner">
                        {t('shipments.shipmentDetail.bids.shipperKycBanner')}{' '}
                        {shipperId != null && (
                          <Link to={`/users/${shipperId}`} state={{ from: `/shipments/${shipmentId}` }}>
                            {t('shipments.shipmentDetail.bids.verifyShipper')}
                          </Link>
                        )}
                      </Notice>
                    </div>
                  );
                })()}
                {renderBidsTable(currentBids, false)}

                {priorBids.length > 0 && (
                  <div className={styles.priorCycleCard}>
                    <div
                      className={styles.priorCycleHeader}
                      onClick={() => setShowPriorCycles((prev) => !prev)}
                    >
                      <h4 className={styles.priorCycleTitle}>
                        <StatusIcon name="history" size={14} />{' '}
                        {t('shipments.shipmentDetail.bids.priorHistory', { count: priorBids.length })}
                      </h4>
                      <span className={styles.priorCycleToggle}>
                        {showPriorCycles ? 'Hide ▲' : 'Show ▼'}
                      </span>
                    </div>
                    {showPriorCycles && renderBidsTable(priorBids, true)}
                  </div>
                )}
              </div>
            );
          })()}

        {activeTab === 'documents' && canRenderTab && (
          <div className={styles.card}>
            <div className={styles.cardHeaderRow}>
              <h3 className={styles.cardTitle}>{t('shipments.shipmentDetail.cards.complianceVerificationDocuments')}</h3>
              <button className={styles.addRowBtn} onClick={() => setIsAddDocOpen(true)}>
                {t('shipments.shipmentDetail.documents.add')}
              </button>
            </div>
            {!documents || documents.length === 0 ? (
              <p className={styles.empty}>{t('shipments.shipmentDetail.empty.noTransportDocumentsUploaded')}</p>
            ) : (
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>{t('shipments.shipmentDetail.table.docId')}</th>
                    <th>{t('shipments.shipmentDetail.table.type')}</th>
                    <th>{t('shipments.shipmentDetail.table.status')}</th>
                    <th>{t('shipments.shipmentDetail.table.uploadedAt')}</th>
                    <th>{t('shipments.shipmentDetail.table.rejectionReason')}</th>
                    <th>{t('shipments.shipmentDetail.table.action')}</th>
                  </tr>
                </thead>
                <tbody>
                  {documents.map((d) => (
                    <tr key={d.id}>
                      <td className={styles.mono}>#{d.id}</td>
                      <td>{codeLabel('documentType', d.type)}</td>
                      <td>{codeLabel('documentStatus', d.status)}</td>
                      <td>{fmtDate(d.uploadedAt)}</td>
                      <td className={styles.rejectionText}>{d.rejectionReason || '—'}</td>
                      <td>
                        <button
                          className={styles.reviewDocBtn}
                          onClick={() => {
                            setSelectedDoc(d);
                            setDocumentReason('');
                            setDocumentError(null);
                          }}
                        >
                          {t('shipments.shipmentDetail.documents.review')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {activeTab === 'tracking' && canRenderTab && (
          <div className={styles.trackingGrid}>
            {(() => {
              const pings = tracking?.filter((ev) => !ev.isManualMilestone);
              const latestPing =
                pings && pings.length > 0
                  ? pings
                      .slice()
                      .sort(
                        (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
                      )[0]
                  : undefined;
              const isTerminalStatus = ['COMPLETED', 'DELIVERED', 'CANCELLED', 'EXPIRED'].includes(
                shipment.status,
              );
              return (
                <>
                  {latestPing &&
                    (() => {
                      const pingLoc = formatTrackingLocation(
                        latestPing.locationName,
                        latestPing.latitude,
                        latestPing.longitude,
                      );
                      const mapsUrl = getGoogleMapsUrl(latestPing.latitude, latestPing.longitude);

                      return (
                        <div className={styles.card} style={{ marginBottom: 16 }}>
                          <h3 className={styles.cardTitle}>{t('shipments.shipmentDetail.cards.latestLivePosition')}</h3>
                          <div style={{ marginTop: 8 }}>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 10,
                                flexWrap: 'wrap',
                              }}
                            >
                              <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)' }}>
                                {pingLoc}
                              </span>
                              {mapsUrl && (
                                <>
                                  <span style={{ color: 'var(--muted)', fontSize: 13 }}>•</span>
                                  <a
                                    href={mapsUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                      fontSize: 13,
                                      color: 'var(--accent)',
                                      textDecoration: 'none',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: 4,
                                      fontWeight: 500,
                                    }}
                                  >
                                    {t('shipments.shipmentDetail.tracking.viewOnMaps')}
                                  </a>
                                </>
                              )}
                            </div>
                            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>
                              {t('shipments.shipmentDetail.tracking.recordedAt', { time: fmtDate(latestPing.timestamp) })}
                            </div>
                          </div>
                        </div>
                      );
                    })()}

                  <div className={styles.stopHistory}>
                    <StopHistoryCard shipmentId={shipmentId} />
                  </div>

                  <div className={styles.card}>
                    <div className={styles.cardHeaderRow}>
                      <h3 className={styles.cardTitle}>{t('shipments.shipmentDetail.cards.milestoneTimeline')}</h3>
                      {shipment.assignment && !isTerminalStatus && (
                        <button
                          className={styles.addRowBtn}
                          onClick={() => setIsAddEventOpen(true)}
                        >
                          {t('shipments.shipmentDetail.tracking.addEvent')}
                        </button>
                      )}
                    </div>
                    <div className={styles.timeline}>
                      {!tracking || tracking.length === 0 ? (
                        <p className={styles.empty}>{t('shipments.shipmentDetail.empty.noTrackingMilestonesLoggedYet')}</p>
                      ) : (
                        tracking.map((ev) => {
                          const tLoc = formatTrackingLocation(ev.locationName, ev.latitude, ev.longitude);
                          const mapsUrl = getGoogleMapsUrl(ev.latitude, ev.longitude);
                          const sourceNote = trackingEventSourceNote(ev);

                          return (
                            <div key={ev.id} className={styles.timelineItem}>
                              <div className={styles.timelinePoint} />
                              <div className={styles.timelineLine} />
                              <div className={styles.timelineBody}>
                                <div className={styles.timelineTime}>{fmtDate(ev.timestamp)}</div>
                                <div className={styles.timelineTitle} data-testid={`milestone-title-${ev.id}`}>{trackingEventTitle(ev)}</div>
                                {sourceNote && (
                                  <div className={`${styles.timelineDesc} ${styles.timelineSource}`}>
                                    {sourceNote}
                                  </div>
                                )}
                                <div className={styles.timelineDesc}>
                                  <div
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 8,
                                      flexWrap: 'wrap',
                                    }}
                                  >
                                    <span>{tLoc}</span>
                                    {mapsUrl && (
                                      <>
                                        <span style={{ color: 'var(--muted)', fontSize: 12 }}>
                                          •
                                        </span>
                                        <a
                                          href={mapsUrl}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          style={{
                                            fontSize: 12,
                                            color: 'var(--accent)',
                                            textDecoration: 'none',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 3,
                                            fontWeight: 500,
                                          }}
                                        >
                                          {t('shipments.shipmentDetail.tracking.viewOnMaps')}
                                        </a>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {activeTab === 'assignment' && canRenderTab && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {/* Assigned Driver & Vehicle Hero Card */}
            {shipment.assignment && (
              <div
                data-testid="assigned-driver-hero"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 16,
                  padding: '14px 18px',
                  backgroundColor: 'var(--surface)',
                  border: '1px solid var(--line)',
                  borderRadius: 12,
                  boxShadow: 'var(--shadow-card)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 260 }}>
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: '50%',
                      backgroundColor: 'var(--ink)',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 700,
                      fontSize: 16,
                      flexShrink: 0,
                    }}
                  >
                    {(
                      shipment.assignment.driverName
                        ?.trim()
                        .split(/\s+/)
                        .map((n) => n[0])
                        .slice(0, 2)
                        .join('') || 'DR'
                    ).toUpperCase()}
                  </div>
                  <div>
                    <div
                      style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}
                    >
                      <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)' }}>
                        {shipment.assignment.driverName ??
                          (shipment.driverId ? `Driver #${shipment.driverId}` : 'Assigned Driver')}
                      </span>
                      <span
                        style={{
                          backgroundColor: 'var(--action-primary-soft)',
                          color: 'var(--accent)',
                          fontSize: 11,
                          fontWeight: 700,
                          padding: '2px 7px',
                          borderRadius: 4,
                        }}
                      >
                        {t('shipments.shipmentDetail.assignment.assignedDriver')}
                      </span>
                      {shipment.assignment.status && (
                        <span
                          style={{
                            backgroundColor:
                              shipment.assignment.status === 'ACCEPTED' ||
                              shipment.assignment.status === 'ACTIVE'
                                ? 'var(--status-green-bg)'
                                : 'var(--status-gray-bg)',
                            color:
                              shipment.assignment.status === 'ACCEPTED' ||
                              shipment.assignment.status === 'ACTIVE'
                                ? 'var(--status-green-text)'
                                : 'var(--status-gray-text)',
                            fontSize: 11,
                            fontWeight: 600,
                            padding: '2px 7px',
                            borderRadius: 4,
                          }}
                        >
                          {codeLabel('assignmentStatus', shipment.assignment.status)}
                        </span>
                      )}
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        marginTop: 4,
                        fontSize: 13,
                        color: 'var(--ink-soft)',
                        flexWrap: 'wrap',
                      }}
                    >
                      {shipment.assignment.driverPhone && (
                        <span>
                          📞{' '}
                          <a
                            href={`tel:${shipment.assignment.driverPhone}`}
                            style={{
                              color: 'var(--accent)',
                              textDecoration: 'none',
                              fontWeight: 500,
                            }}
                          >
                            {shipment.assignment.driverPhone}
                          </a>
                        </span>
                      )}
                      {shipment.assignment.vehicleRegistrationNumber && (
                        <span
                          style={{
                            fontFamily: 'var(--font-mono)',
                            fontWeight: 600,
                            color: 'var(--ink)',
                          }}
                        >
                          🚛 {shipment.assignment.vehicleRegistrationNumber}
                          {shipment.assignment.vehicleId && (
                            <span style={{ color: 'var(--muted)', fontWeight: 400, marginLeft: 4 }}>
                              (#{shipment.assignment.vehicleId})
                            </span>
                          )}
                        </span>
                      )}
                      {shipment.assignment.fleetManagerName && (
                        <span style={{ color: 'var(--muted)' }}>
                          {t('shipments.shipmentDetail.assignment.partner')} <strong>{shipment.assignment.fleetManagerName}</strong>
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {!isDeliveredOrTerminal(shipment.status) && (
                  <button
                    className={styles.btnControl}
                    style={{ padding: '6px 14px', fontSize: 12.5 }}
                    onClick={() => handleOpenAssignDriver('edit_details')}
                    title={t('shipments.shipmentDetail.assignment.manageHint')}
                  >
                    {t('shipments.shipmentDetail.assignment.manageDriver')}
                  </button>
                )}
              </div>
            )}
            <div className={styles.card}>
              <div className={styles.cardHeaderRow}>
                <h3 className={styles.cardTitle}>{t('shipments.shipmentDetail.cards.carrierAssignmentStatus')}</h3>
                {shipment.status === 'AWARDED' && !shipment.assignment && (
                  <button
                    className={styles.addRowBtn}
                    onClick={() => handleOpenAssignDriver('change_driver')}
                  >
                    {t('shipments.shipmentDetail.assignment.assignDriverAdd')}
                  </button>
                )}
              </div>
              {shipment.assignment ? (
                <div className={styles.assignmentDetails}>
                  <div className={styles.detailRow}>
                    <span className={styles.label}>{t('shipments.shipmentDetail.labels.assignmentId')}</span>
                    <span className={`${styles.val} ${styles.mono}`}>
                      #{shipment.assignment.id}
                    </span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.label}>{t('shipments.shipmentDetail.labels.fleetManagerId')}</span>
                    <span className={`${styles.val} ${styles.mono}`}>
                      #{shipment.assignment.fleetManagerId}
                    </span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.label}>{t('shipments.shipmentDetail.labels.assignedDriverId')}</span>
                    <span className={`${styles.val} ${styles.mono}`}>
                      #{shipment.assignment.driverId}
                    </span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.label}>{t('shipments.shipmentDetail.labels.vehicleId')}</span>
                    <span className={`${styles.val} ${styles.mono}`}>
                      #{shipment.assignment.vehicleId}
                    </span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.label}>{t('shipments.shipmentDetail.labels.assignedAt')}</span>
                    <span className={styles.val}>{fmtDate(shipment.assignment.assignedAt)}</span>
                  </div>
                  <DriverAcceptanceRow status={shipment.status} assignment={shipment.assignment} />
                  <div className={styles.detailRow}>
                    <span className={styles.label}>{t('shipments.shipmentDetail.labels.status')}</span>
                    <span className={styles.val}>{codeLabel('assignmentStatus', shipment.assignment.status)}</span>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <p className={styles.empty}>{t('shipments.shipmentDetail.empty.noCarrierAssignmentAssignedYet')}</p>
                  {shipment.status === 'AWARDED' && (
                    <div>
                      <button
                        className={`${styles.btnControl} ${styles.btnAssign}`}
                        onClick={() => setIsAssignDriverOpen(true)}
                      >
                        {t('shipments.shipmentDetail.assignment.assignDriverNow')}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Shipment Cancel Dialog */}
      <ConfirmDialog
        isOpen={confirmAction === 'cancel'}
        help="trip-problems"
        title={t('shipments.shipmentDetail.dialogs.cancelTitle')}
        description="Are you sure you want to cancel this shipment? This action is irreversible and will notify the partner and shipper."
        confirmText="Cancel Shipment"
        cancelText="Keep Shipment"
        variant="danger"
        loading={cancelMut.isLoading}
        errorMessage={dialogError}
        onConfirm={() => {
          setDialogError(null);
          cancelMut.mutate(undefined);
        }}
        onCancel={() => setConfirmAction(null)}
      />

      {/* Shipment Complete Dialog */}
      <ConfirmDialog
        isOpen={confirmAction === 'complete'}
        help="trip-status"
        title={t('shipments.shipmentDetail.dialogs.completeTitle')}
        description="Are you sure you want to mark this shipment as completed? Make sure POD documents are uploaded and verified."
        confirmText="Complete"
        cancelText="Cancel"
        variant="success"
        loading={completeMut.isLoading}
        errorMessage={dialogError}
        onConfirm={() => {
          setDialogError(null);
          completeMut.mutate(undefined);
        }}
        onCancel={() => setConfirmAction(null)}
      />

      {/* Truck stopped / Edit stop (dnm-work#165) */}
      <TruckStoppedDialog
        isOpen={stopSheet !== null}
        shipmentId={shipmentId}
        stop={stopSheet === 'edit' ? (shipment.openStop ?? null) : null}
        loading={openStopMut.isLoading || editStopMut.isLoading}
        errorMessage={stopError}
        onConfirm={(body) => {
          setStopError(null);
          if (stopSheet === 'edit' && shipment.openStop) {
            editStopMut.mutate({ stopId: shipment.openStop.id, body: body as EditStopRequest });
          } else {
            openStopMut.mutate(body as OpenStopRequest);
          }
        }}
        onCancel={() => {
          setStopError(null);
          setStopSheet(null);
        }}
      />

      {/* Moving again: closes the open stop; the trip keeps its step (R53) */}
      <ConfirmDialog
        isOpen={confirmAction === 'movingAgain'}
        help="truck-stopped"
        title={t('stops.moving.title')}
        description={t('stops.moving.description', {
          shipmentId,
          stage: shipmentStatusLabel(shipment.status),
        })}
        confirmText={t('stops.moving.confirm')}
        cancelText={t('common.actions.cancel')}
        variant="primary"
        loading={movingAgainMut.isLoading}
        errorMessage={dialogError}
        onConfirm={() => {
          setDialogError(null);
          if (shipment.openStop) movingAgainMut.mutate(shipment.openStop.id);
        }}
        onCancel={() => setConfirmAction(null)}
      />

      {/* Shipment Repost Dialog */}
      <ConfirmDialog
        isOpen={confirmAction === 'repost'}
        help="trip-problems"
        title={t('shipments.shipmentDetail.dialogs.repostTitle')}
        description="Are you sure you want to repost this shipment to the marketplace? This will reset the status to UNASSIGNED and open bidding."
        confirmText="Repost"
        cancelText="Cancel"
        variant="primary"
        loading={repostMut.isLoading}
        errorMessage={dialogError}
        onConfirm={() => {
          setDialogError(null);
          repostMut.mutate(undefined);
        }}
        onCancel={() => setConfirmAction(null)}
      />

      {/* Recalculate Distance Dialog (POSTED: a big change closes pending bids) */}
      <ConfirmDialog
        isOpen={confirmAction === 'recomputeDistance'}
        help="edit-shipment"
        title={t('shipments.shipmentDetail.dialogs.recomputeDistanceTitle')}
        description="If the distance changes by more than 50 km, pending bids will close."
        confirmText="Recalculate"
        cancelText="Cancel"
        variant="primary"
        loading={recomputeDistanceMut.isLoading}
        onConfirm={recomputeDistance}
        onCancel={() => setConfirmAction(null)}
      />

      {/* Reverse Award Dialog: a reason is required (#230) */}
      {SHIPPER_ZERO_CALLS && (
        <RecordDelaySheet
          isOpen={delaySheetOpen}
          shipmentId={shipment.id}
          loading={recordDelayMut.isLoading}
          errorMessage={delayError}
          onConfirm={(body) => {
            setDelayError(null);
            recordDelayMut.mutate(body);
          }}
          onCancel={() => setDelaySheetOpen(false)}
        />
      )}
      <ReverseAwardDialog
        isOpen={confirmAction === 'reverseAward'}
        shipmentId={shipment.id}
        loading={reverseAwardMut.isLoading}
        errorMessage={dialogError}
        onConfirm={(choice) => {
          setDialogError(null);
          reverseAwardMut.mutate(choice);
        }}
        onCancel={() => setConfirmAction(null)}
      />

      {/* Award summary (Accept Bid) Dialog */}
      <ConfirmDialog
        isOpen={confirmAction === 'acceptBid'}
        help="bids-and-award"
        wide
        title={
          selectedBid
            ? `Award shipment #${shipment.id} to ${selectedBidLabel}`
            : `Award shipment #${shipment.id}`
        }
        description={
          selectedBid ? (
            <AwardSummary
              bid={selectedBid}
              bids={bids ?? []}
              currentPostingCycle={shipment.currentPostingCycle}
              laneHint={<LaneHint originCity={shipment.originCity} destinationCity={shipment.destinationCity} />}
            />
          ) : (
            `Award this shipment to bid #${selectedBidId}? This assigns it to the bidder.`
          )
        }
        confirmText="Award"
        cancelText="Cancel"
        variant="success"
        loading={acceptBidMut.isLoading}
        errorMessage={dialogError}
        secondaryAction={
          selectedBid
            ? {
                label: 'Change shipper price',
                onClick: () => {
                  setConfirmAction(null);
                  setSelectedBidId(null);
                  setDialogError(null);
                  setBidModal(selectedBid);
                },
              }
            : undefined
        }
        onConfirm={() => {
          setDialogError(null);
          acceptBidMut.mutate(selectedBidId!);
        }}
        onCancel={() => {
          setConfirmAction(null);
          setSelectedBidId(null);
        }}
      />

      {/* Document Preview Modal */}
      {selectedDoc &&
        (() => {
          const urlWithoutQuery = (selectedDoc.downloadUrl || selectedDoc.fileUrl)
            .split('?')[0]
            .toLowerCase();
          const isImage =
            urlWithoutQuery.endsWith('.png') ||
            urlWithoutQuery.endsWith('.jpg') ||
            urlWithoutQuery.endsWith('.jpeg') ||
            urlWithoutQuery.endsWith('.webp') ||
            urlWithoutQuery.endsWith('.gif');
          return (
            <div
              className={styles.modalBackdrop}
              onClick={() => {
                if (!documentGuard.current) setSelectedDoc(null);
              }}
            >
              <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                  <DialogTitleRow
                    context="shipment-documents"
                    title={t('shipments.shipmentDetail.docModal.title', { id: selectedDoc.id, type: selectedDoc.type })}
                  >
                    <h3 className={styles.modalTitle}>
                      {t('shipments.shipmentDetail.docModal.title', { id: selectedDoc.id, type: selectedDoc.type })}
                    </h3>
                  </DialogTitleRow>
                  <button
                    className={styles.modalClose}
                    onClick={() => {
                      if (!documentGuard.current) setSelectedDoc(null);
                    }}
                  >
                    ✕
                  </button>
                </div>
                <div className={styles.modalBody}>
                  <div className={styles.modalContentSplit}>
                    <div className={styles.modalPreviewCol}>
                      <div className={styles.previewContainer}>
                        {isImage ? (
                          <img
                            src={selectedDoc.downloadUrl || selectedDoc.fileUrl}
                            alt={t('shipments.shipmentDetail.docModal.document')}
                            className={styles.previewImage}
                          />
                        ) : (
                          <iframe
                            src={selectedDoc.downloadUrl || selectedDoc.fileUrl}
                            title={t('shipments.shipmentDetail.docModal.document')}
                            className={styles.previewFrame}
                          />
                        )}
                      </div>
                    </div>
                    <div className={styles.modalInfoCol}>
                      <div className={styles.docInfoGrid}>
                        <span className={styles.infoLabel}>{t('shipments.shipmentDetail.docModal.documentId')}</span>
                        <span className={styles.infoVal}>#{selectedDoc.id}</span>
                        <span className={styles.infoLabel}>{t('shipments.shipmentDetail.docModal.shipmentId')}</span>
                        <span className={styles.infoVal}>#{selectedDoc.shipmentId}</span>
                        <span className={styles.infoLabel}>{t('shipments.shipmentDetail.docModal.docType')}</span>
                        <span className={styles.infoVal}>{codeLabel('documentType', selectedDoc.type)}</span>
                        <span className={styles.infoLabel}>{t('shipments.shipmentDetail.docModal.status')}</span>
                        <span className={styles.infoVal}>{codeLabel('documentStatus', selectedDoc.status)}</span>
                        {selectedDoc.rejectionReason && (
                          <>
                            <span className={styles.infoLabel}>{t('shipments.shipmentDetail.docModal.rejectionReason')}</span>
                            <span className={styles.infoVal}>{selectedDoc.rejectionReason}</span>
                          </>
                        )}
                      </div>
                      <a
                        href={selectedDoc.downloadUrl || selectedDoc.fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.previewLink}
                      >
                        {t('shipments.shipmentDetail.docModal.openInNewTab')}
                      </a>
                      <p>
                        {t('shipments.shipmentDetail.docModal.uploaded', {
                          time: formatIstDateTime(selectedDoc.uploadedAt),
                          user: selectedDoc.uploadedById,
                        })}
                      </p>
                      {selectedDoc.verifiedAt && (
                        <p>
                          {t('shipments.shipmentDetail.docModal.decided', {
                            decision:
                              selectedDoc.status === 'REJECTED'
                                ? t('shipments.shipmentDetail.docModal.rejected')
                                : t('shipments.shipmentDetail.docModal.decisionRecorded'),
                            time: formatIstDateTime(selectedDoc.verifiedAt),
                            user: selectedDoc.verifiedById ?? t('shipments.shipmentDetail.docModal.unavailable'),
                          })}
                        </p>
                      )}
                      {selectedDoc.status !== 'VERIFIED' && (
                        <div>
                          <DocumentDecisionButtons
                            documentId={selectedDoc.id}
                            pending={documentBusy}
                            reason={documentReason}
                            onReasonChange={setDocumentReason}
                            error={documentError}
                            onDecision={async (approve) => {
                              if (documentGuard.current || selectedDoc.status === 'VERIFIED')
                                return;
                              documentGuard.current = true;
                              setDocumentBusy(true);
                              setDocumentError(null);
                              try {
                                await verifyDocument(
                                  selectedDoc.id,
                                  approve,
                                  approve ? undefined : documentReason.trim(),
                                );
                                toast.success(
                                  approve
                                    ? t('documents.decisionToast.verified', { id: selectedDoc.id })
                                    : t('documents.decisionToast.rejected', { id: selectedDoc.id }),
                                );
                                setSelectedDoc(null);
                                await refetchDocuments();
                                await refetchMoney();
                                await refetchShipment();
                                await Promise.all([
                                  documentClient.invalidateQueries({ queryKey: ['payments'] }),
                                  documentClient.invalidateQueries({
                                    queryKey: ['admin', 'work'],
                                  }),
                                  documentClient.invalidateQueries({
                                    queryKey: ['admin', 'scorecard'],
                                  }),
                                ]);
                              } catch {
                                setDocumentError(
                                  'We could not save your decision. Refresh the document status, then try again or call support.',
                                );
                              } finally {
                                documentGuard.current = false;
                                setDocumentBusy(false);
                              }
                            }}
                          />
                        </div>
                      )}
                      <div className={styles.modalActions}>
                        <button
                          className={styles.closeBtn}
                          disabled={documentBusy}
                          onClick={() => {
                            if (!documentGuard.current) setSelectedDoc(null);
                          }}
                        >
                          {t('common.actions.close')}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

      {/* Shipper profile drawer — opens in place over the shipment */}
      {shipperDrawerOpen && (
        <UserDetailDrawer userId={shipment.shipperId} onClose={() => setShipperDrawerOpen(false)} />
      )}

      {isEditShipmentOpen && (
        <ShipmentFormModal shipment={shipment} onClose={() => setIsEditShipmentOpen(false)} />
      )}

      {editStop && (
        <EditStopAddressModal shipment={shipment} stop={editStop} onClose={() => setEditStop(null)} />
      )}

      {shipperFreightOpen && shipment && (
        <EditShipperFreightDialog shipment={shipment} onClose={() => setShipperFreightOpen(false)} />
      )}
      {partnerPriceOpen && shipment && (
        <EditPartnerPriceDialog shipment={shipment} onClose={() => setPartnerPriceOpen(false)} />
      )}

      {bidModal !== 'closed' && (
        <BidFormModal
          shipmentId={shipmentId}
          bid={bidModal === 'create' ? null : bidModal}
          initialBidderId={bidModal === 'create' ? (bidPrefillId ?? undefined) : undefined}
          onClose={() => {
            setBidModal('closed');
            setBidPrefillId(null);
          }}
        />
      )}

      {isAddEventOpen && (
        <TrackingEventModal
          shipmentId={shipmentId}
          shipmentStatus={shipment.status}
          onClose={() => setIsAddEventOpen(false)}
        />
      )}

      {isAddDocOpen && (
        <DocumentUploadModal shipmentId={shipmentId} onClose={() => setIsAddDocOpen(false)} />
      )}

      {isAssignDriverOpen && (
        <AssignDriverModal
          shipment={shipment}
          initialMode={assignDriverInitialMode}
          onClose={() => setIsAssignDriverOpen(false)}
        />
      )}

      {advanceStatus && (
        <AdvanceStatusModal
          shipment={shipment}
          targetStatus={advanceStatus}
          onClose={() => setAdvanceStatus(null)}
        />
      )}

      {isInTransitLocationOpen && (
        <InTransitLocationModal
          shipmentId={shipmentId}
          onClose={() => setIsInTransitLocationOpen(false)}
        />
      )}

      {selectedInterestId && (
        <InterestDetailModal
          interestId={selectedInterestId}
          onClose={() => setSelectedInterestId(null)}
          onConvertToBid={(detail) => {
            setSelectedInterestId(null);
            setInterestToConvert(detail);
          }}
        />
      )}

      {interestToConvert && (
        <BidFormModal
          shipmentId={shipmentId}
          initialBidderId={interestToConvert.vendorId}
          interest={interestToConvert}
          onClose={() => setInterestToConvert(null)}
        />
      )}

      {/* On-demand notification history drawer — mounts only when the button is clicked */}
      {notificationDrawerOpen && (
        <ShipmentNotifications
          shipmentId={shipmentId}
          currentPostingCycle={shipment.currentPostingCycle}
          onClose={() => setNotificationDrawerOpen(false)}
        />
      )}
      {reviewPartnerChargeId != null && <ReviewPartnerChargeFlow chargeId={reviewPartnerChargeId} isManager={isManager} invoiceIssued={Boolean(moneySummary?.issuedInvoice)} onClose={() => {setReviewPartnerChargeId(null); void refetchMoney();}} />}
      {isAddChargeOpen && (
        <AddChargeDialog
          shipmentId={shipmentId}
          shipperLockedReason={
            moneySummary?.issuedInvoice ? 'Invoice issued: cancel it to add' : undefined
          }
          partnerLockedReason={
            moneySummary &&
            ['BALANCE_IN_APPROVAL', 'BALANCE_TO_PAY', 'PAID'].includes(moneySummary.partnerStatus)
              ? 'Balance already requested'
              : undefined
          }
          onClose={() => setIsAddChargeOpen(false)}
          submitting={addChargeMut.isPending}
          onSubmit={async (request) => {
            await addChargeMut.mutateAsync({ shipmentId, request });
            setIsAddChargeOpen(false);
            await refetchMoney();
          }}
        />
      )}
      {chargeReason && (
        <ReasonDialog
          title={chargeReason.kind === 'reject' ? 'Reject charge' : 'Void charge'}
          actionLabel={chargeReason.kind === 'reject' ? 'Reject charge' : 'Void charge'}
          help="reject-charge"
          error={chargeActionError}
          submitting={rejectChargeMut.isPending || voidChargeMut.isPending}
          onCancel={() => setChargeReason(null)}
          onSubmit={async (reason) => {
            setChargeActionError(null);
            try {
              if (chargeReason.kind === 'reject')
                await rejectChargeMut.mutateAsync({ chargeId: chargeReason.chargeId, reason });
              else await voidChargeMut.mutateAsync({ chargeId: chargeReason.chargeId, reason });
              setChargeReason(null);
              await refetchMoney();
            } catch (error) {
              setChargeActionError(mapPaymentError(error));
            }
          }}
        />
      )}
      {billDialogOpen && (
        <PartnerBillDialog
          initialValue={moneySummary?.partnerBillNumber ?? ''}
          submitting={billSaving}
          error={billError}
          onCancel={() => {
            if (billGuard.current) return;
            setBillError(null);
            setBillDialogOpen(false);
          }}
          onSubmit={async (billNumber) => {
            if (billGuard.current) return;
            billGuard.current = true;
            setBillSaving(true);
            setBillError(null);
            try {
              await setPartnerBill(shipmentId, billNumber);
              setBillDialogOpen(false);
              await refetchMoney();
            } catch (error) {
              // A mapped DNM message when there is one, never raw request text.
              const mapped = mapPaymentError(error);
              setBillError(
                error instanceof Error && mapped === error.message
                  ? t('payments.partnerBillDialog.saveFailed')
                  : mapped,
              );
            } finally {
              billGuard.current = false;
              setBillSaving(false);
            }
          }}
        />
      )}
    </div>
  );
}
